import { MeshBasicMaterial, Mesh, CircleGeometry, PlaneGeometry } from "three";
import { VBase } from "./VBase";
import VUtil from "../VUtils";

const defaultOptions = {
  map: null,
  color: 0x676767,
  fillColor: 0x00ffff,
  fillOpacity: 1,
  fillMap: null,
  opacity: 1,
  handleOpacity: 1,
  handleMap: null,
  handleColor: 0x00ffff,
  width: 1.8,
  height: 0.05,
  radius: 0.02,
  onValueChange: null,
  onValueChangeStart: null,
  onValueChangeEnd: null,
  disabledColor: 0xc8c8c8,
  enabledCb: null,
};

/* 
  使用案例
    // 加载贴图
    const sliderBgPath = "/assets/vrUI/skyPanel/sliderBg1.png";
      const sliderHandlePath = "/assets/vrUI/skyPanel/sliderHandle.png";
      const sliderBg = await this.manager.loadManager.loadTexture(sliderBgPath);
      const sliderHandle = await this.manager.loadManager.loadTexture(
        sliderHandlePath
      );

      const slider = this.uiLib.createSlider({
        map: sliderBg,
        color: 0xffffff,
        opacity: 0.8,
        handleMap: sliderHandle,
        handleColor: 0xffffff,
        fillOpacity: 0.5,
        width: 0.75,
        height: 0.03,
        radius: 0.015,
      });
     // 设置进度条当前数值
      slider.setProgressValue(0.5);
      // 注册进度值改变事件 可选
      slider.addOnValueChangeEvent((v) => {
        console.log("进度值在改变中：" + v);
      });
      // 注册进度值改变开始事件 可选
      slider.addOnValueChangeStartEvent((v) => {
        console.log("进度值改变开始：" + v);
      });
      // 注册进度值改变结束事件 可选
      slider.addOnValueChangeEndEvent((v) => {
        console.log("进度值改变结束：" + v);
      });
      // 注册光标进入handle事件 可选
      slider.addHandlerHoverEnterEvent((handle) => {
        console.log("光标进入handle");
      });
      // 注册光标离开handle事件 可选
      slider.addHandlerHoverExitEvent((handle) => {
        console.log("光标离开handle");
      });

      this.uiLib.addUIToScene(slider);

*/

export class VSlider extends VBase {
  /**
   * 带有填充的滑动条，如音量滑动条
   *   options:
   * {
   *      priority: 优先级，默认为1，值越小越靠前
   *      renderOrder: 渲染顺序，默认为99998
   *      map: 进度条背景贴图，非地址
   *      color: 进度条背景色，默认为0x676767
   *      opacity: 进度条背景透明度，默认为1
   *      handleOpacity: handle透明度，默认为1
   *      fillColor: 进度条填充颜色，默认为0x00ffff
   *      fillOpacity: 进度条填充透明度，默认为1
   *      fillMap: 进度条填充贴图，非地址
   *      handleMap: 滑块贴图，非地址
   *      handleColor: 滑块颜色，默认为0x00ffff
   *      width: 进度条宽度，默认为1.8
   *      height: 进度条高度，默认为0.05
   *      radius: 圆角矩形半径，默认为0.02
   *      disabledColor:  ui对象禁用颜色，默认为 0xC8C8C8
   * }
   * @param {*} options
   */
  constructor(options, vuiLib) {
    if (!options) options = {};

    const tempOptions = {
      priority: options.priority !== undefined ? options.priority : 1,
      renderOrder:
        options.renderOrder !== undefined ? options.renderOrder : 99998,
      map: options.map ? options.map : defaultOptions.map,
      handleMap: options.handleMap
        ? options.handleMap
        : defaultOptions.handleMap,
      handleColor:
        options.handleColor !== undefined
          ? options.handleColor
          : defaultOptions.handleColor,
      color: options.color !== undefined ? options.color : defaultOptions.color,
      opacity:
        options.opacity !== undefined
          ? options.opacity
          : defaultOptions.opacity,
      handleOpacity:
        options.handleOpacity !== undefined
          ? options.handleOpacity
          : defaultOptions.handleOpacity,
      fillOpacity:
        options.fillOpacity !== undefined
          ? options.fillOpacity
          : defaultOptions.fillOpacity,
      fillColor: options.fillColor
        ? options.fillColor
        : defaultOptions.fillColor,
      fillMap: options.fillMap ? options.fillMap : defaultOptions.fillMap,
      width: options.width !== undefined ? options.width : defaultOptions.width,
      height:
        options.height !== undefined ? options.height : defaultOptions.height,
      radius:
        options.radius !== undefined ? options.radius : defaultOptions.radius,
      disabledColor:
        options.disabledColor !== undefined
          ? options.disabledColor
          : defaultOptions.disabledColor,
    };

    super(tempOptions, vuiLib);
    this._vType = "VSlider";

    this.lastTarget = 0;
    this.value = 0; // 当前滑块所在位置，0到1的小数

    this._otherProps = {
      handleMap: tempOptions.handleMap,
      handleColor: tempOptions.handleColor,
      handleOpacity: tempOptions.handleOpacity,
      fillOpacity: tempOptions.fillOpacity,
      fillColor: tempOptions.fillColor,
      fillMap: tempOptions.fillMap,
      onValueChangeStart: [],
      onValueChangeEnd: [],
      onValueChange: [],
      handleHoverEnter: [],
      handleHoverExit: [],
    };

    this.name = "VSlider";

    // fill
    var vertexParsChunk = `
    #include <common>
    varying float localPosX;
  `;
    var vertexChunk = `
    #include <fog_vertex>
    localPosX = position.x;
  `;
    var fragmentParsChunk = `
            #include <common>
            uniform float limit;
            varying float localPosX;
          `;
    var diffuseColorChunk = `
              if(localPosX>limit)
              {
                discard;
              }

              vec4 diffuseColor = vec4( diffuse, opacity );
           `;
    const geo1 = VUtil.createRectangleGeometry(
      this._baseProps.width,
      this._baseProps.height,
      this._baseProps.radius
    );
    const mat1 = new MeshBasicMaterial({
      color: this._otherProps.fillColor,
      transparent: true,
      opacity: this._otherProps.fillOpacity,
      depthTest: this.depthTest,
    });
    VUtil.convertSRGBToLinear(mat1);
    mat1.userData.limit = { value: 0 };
    mat1.onBeforeCompile = (shader) => {
      shader.vertexShader = shader.vertexShader
        .replace("#include <common>", vertexParsChunk)
        .replace("#include <fog_vertex>", vertexChunk);

      shader.fragmentShader = shader.fragmentShader
        .replace("#include <common>", fragmentParsChunk)
        .replace(
          "vec4 diffuseColor = vec4( diffuse, opacity );",
          diffuseColorChunk
        );
      shader.uniforms.limit = mat1.userData.limit;
    };
    this.fill = new Mesh(geo1, mat1);
    this.fill.renderOrder = this._baseProps.renderOrder + 1;
    this.add(this.fill);

    // handle
    let geo2 = new CircleGeometry(this._baseProps.height * 1.5, 32);
    if (this._otherProps.handleMap)
      geo2 = new PlaneGeometry(
        this._baseProps.height * 3,
        this._baseProps.height * 3
      );
    let mat2 = new MeshBasicMaterial({
      color: this._otherProps.handleColor,
      transparent: true,
      map: this._otherProps.handleMap,
      opacity: this._otherProps.handleOpacity,
      depthTest: this.depthTest,
    });
    VUtil.convertSRGBToLinear(mat2);
    this.handle = new Mesh(geo2, mat2);
    this.handle._vType = "SliderHandle";
    this.handle.renderOrder = this._baseProps.renderOrder + 2;
    this.add(this.handle);

    // 注册光标进入事件
    this.handle.onEcsyHoverEnter = () => {
      this._onHandleHoverEnter();
    };
    // 注册光标退出事件
    this.handle.onEcsyHoverExit = () => {
      this._onHandleHoverExit();
    };
    this.handle.position.x = 0;
    this.handle.position.z = 0.001;

    this.mask = new Mesh(
      new PlaneGeometry(8, 8),
      new MeshBasicMaterial({ transparent: true, opacity: 0.5 })
    );
    this.mask.material.visible = false;
    VUtil.convertSRGBToLinear(this.mask.material);

    if (this.vuilib) {
      this.vuilib.addComponents(this.handle, this._priority - 1);
    }

    this.width = this._baseProps.width;
    this.height = this._baseProps.height * 1.5;
    this.position.z = 0.001;
  }

  clear() {
    super.clear();
    this.mask.geometry.dispose();
    this.mask.material.dispose();
  }

  /**
   * 设置handle位置
   * @param {Number} v 0到1的小数
   */
  setProgressValue(v) {
    v = Math.min(1, Math.max(0, v));

    this.handle.position.x =
      this._baseProps.width * v - this._baseProps.width / 2;

    this.value = v;

    for (var i = 0; i < this._otherProps.onValueChange.length; i++) {
      this._otherProps.onValueChange[i](this.value);
    }
  }

  /**
   * 设置进度值，在VUILib中使用，其他地方不需要主动调用
   * @param {Vector3} value 世界坐标
   */
  setValue(value) {
    // 转换到object空间坐标中
    const localPos = this.worldToLocal(value);
    // 限制在width范围内
    let target = localPos.x;
    target = Math.max(
      -this._baseProps.width / 2,
      Math.min(target, this._baseProps.width / 2)
    );
    target = Math.round(target * 1000) / 1000; // 保留3位小数

    if (this.lastTarget === target) return;
    this.lastTarget = target;

    this.fill.material.userData.limit.value = target;
    this.handle.position.x = target;

    this.value = (target + this._baseProps.width / 2) / this._baseProps.width;

    for (var i = 0; i < this._otherProps.onValueChange.length; i++) {
      this._otherProps.onValueChange[i](this.value);
    }
  }

  _onHandleHoverEnter() {
    if (!this.handle || this.isDraging || !this._baseProps.enabled) return;

    this.handle._vrHoverSrcScale = this.handle.scale.clone();
    this.handle.scale.multiplyScalar(1.1);

    for (var i = 0; i < this._otherProps.handleHoverEnter.length; i++) {
      this._otherProps.handleHoverEnter[i](this.handle);
    }
  }

  _onHandleHoverExit() {
    if (!this.handle || this.isDraging || !this._baseProps.enabled) return;

    if (this.handle._vrHoverSrcScale) {
      this.handle.scale.copy(this.handle._vrHoverSrcScale);
      delete this.handle._vrHoverSrcScale;
    }

    for (var i = 0; i < this._otherProps.handleHoverExit.length; i++) {
      this._otherProps.handleHoverExit[i](this.handle);
    }
  }

  onSelectStart() {
    if (!this._baseProps.enabled) return;
    this.mask.position.z = this.vuilib.getCameraDis();
    this.vuilib.camera.add(this.mask);

    this.isDraging = true;

    for (var i = 0; i < this._otherProps.onValueChangeStart.length; i++) {
      this._otherProps.onValueChangeStart[i](this.value);
    }
  }

  onSelectEnd(obj, intersection) {
    if (!this._baseProps || !this._baseProps.enabled) return;

    // 拖拽滑块结束
    if (this.isDraging) {
      this.mask.removeFromParent();

      this.isDraging = false;

      if (this.handle._vrHoverSrcScale) {
        this.handle.scale.copy(this.handle._vrHoverSrcScale);
        delete this.handle._vrHoverSrcScale;
      }
    }

    // 点击到滑动条，设置滑块到点击位置
    else {
      if (intersection) {
        this.setValue(intersection.point);
      }
    }

    for (var i = 0; i < this._otherProps.onValueChangeEnd.length; i++) {
      this._otherProps.onValueChangeEnd[i](this.value);
    }
  }

  getClickable() {
    if (!this.visible || !this._baseProps.enabled || !this.handle) return [];

    let res = [this.handle, this];
    return res;
  }

  /**
   * 增加一个回调函数，当value开始改变调用，参数为当前value值（0-1）
   * @param {*} fun
   */
  addOnValueChangeStartEvent(fun) {
    if (!fun) return;
    this._otherProps.onValueChangeStart.push(fun);
  }

  /**
   * 移除一个 当value开始改变调用 的回调函数，参数为当前value值（0-1）
   * @param {*} fun
   * @returns
   */
  removeOnValueChangeStartEvent(fun) {
    if (!fun) return;
    let index = this._otherProps.onValueChangeStart.indexOf(fun);
    if (index >= 0) {
      this._otherProps.onValueChangeStart.splice(index, 1);
    }
  }

  /**
   * 移除所有 当value开始改变调用 的回调函数
   */
  removeAllOnvalueChangeStartEvent() {
    this._otherProps.onValueChangeStart = [];
  }

  /**
   * 增加一个回调函数，当value改变结束后调用，参数为当前value值（0-1）
   * @param {*} fun
   * @returns
   */
  addOnValueChangeEndEvent(fun) {
    if (!fun) return;
    this._otherProps.onValueChangeEnd.push(fun);
  }

  /**
   * 移除一个 当value改变结束后调用 的回调函数，参数为当前value值（0-1）
   * @param {*} fun
   * @returns
   */
  removeOnValueChangeEndEvent(fun) {
    if (!fun) return;
    let index = this._otherProps.onValueChangeEnd.indexOf(fun);
    if (index >= 0) {
      this._otherProps.onValueChangeEnd.splice(index, 1);
    }
  }

  /**
   * 移除所有 当value改变结束后调用 的回调函数
   */
  removeAllOnvalueChangeEndEvent() {
    this._otherProps.onValueChangeEnd = [];
  }

  /**
   * 增加一个回调函数，当value改变过程中调用，参数为当前value值（0-1）
   * @param {*} fun
   * @returns
   */
  addOnValueChangeEvent(fun) {
    if (!fun) return;
    this._otherProps.onValueChange.push(fun);
  }

  /**
   * 移除一个 当value改变过程中调用 的回调函数，参数为当前value值（0-1）
   * @param {*} fun
   * @returns
   */
  removeOnValueChangeEvent(fun) {
    if (!fun) return;
    let index = this._otherProps.onValueChange.indexOf(fun);
    if (index >= 0) {
      this._otherProps.onValueChange.splice(index, 1);
    }
  }

  /**
   * 移除所有 当value改变过程中调用 的回调函数
   */
  removeAllOnValueChangeEvent() {
    this._otherProps.onValueChange = [];
  }

  /**
   * 增加一个handle光标进入事件，参数为handle对象
   * @param {*} fun
   * @returns
   */
  addHandlerHoverEnterEvent(fun) {
    if (!fun) return;
    this._otherProps.handleHoverEnter.push(fun);
  }

  /**
   * 移除一个handle光标进入事件，参数为handle对象
   * @param {*} fun
   * @returns
   */
  removeHandlerHoverEnterEvent(fun) {
    if (!fun) return;
    let index = this._otherProps.handleHoverEnter.indexOf(fun);
    if (index >= 0) {
      this._otherProps.handleHoverEnter.splice(index, 1);
    }
  }

  /**
   * 移除所有handle光标进入事件
   */
  removeAllHandlerHoverEnterEvent() {
    this._otherProps.handleHoverEnter = [];
  }

  /**
   * 增加一个handle光标退出事件，参数为handle对象
   * @param {*} fun
   */
  addHandlerHoverExitEvent(fun) {
    if (!fun) return;
    this._otherProps.handleHoverExit.push(fun);
  }

  /**
   * 移除一个handle光标退出事件，参数为handle对象
   * @param {*} fun
   */
  removeHandlerHoverExitEvent(fun) {
    if (!fun) return;
    let index = this._otherProps.handleHoverExit.indexOf(fun);
    if (index >= 0) {
      this._otherProps.handleHoverExit.splice(index, 1);
    }
  }

  /**
   * 移除所有handle光标退出事件
   */
  removeAllHandlerHoverExitEvent() {
    this._otherProps.handleHoverExit = [];
  }
}
